import type { CSSProperties } from 'react'

import type { BracketBackgroundPosition, PanOffset } from './types'

export const BRACKET_STAGE_PADDING = 24

type BracketStageInput = {
  bracketHeight: number
  bracketWidth: number
  viewportHeight: number
  viewportWidth: number
  zoom: number
}

type BracketContentPlacementInput = {
  bracketHeight: number
  bracketWidth: number
  position: BracketBackgroundPosition
}

export type BracketStageLayout = {
  pan: PanOffset
  stageHeight: number
  stageWidth: number
}

function finiteOrZero(value: number) {
  return Number.isFinite(value) ? value : 0
}

export function getInitialBracketZoom({
  bracketHeight,
  bracketWidth,
  viewportHeight,
  viewportWidth,
}: Omit<BracketStageInput, 'zoom'>): number {
  if (
    ![bracketWidth, bracketHeight, viewportWidth, viewportHeight].every(
      (value) => Number.isFinite(value) && value > 0,
    )
  )
    return 1

  // 先适配首屏；过大的签表保留可读比例，通过拖拽查看其余比赛。
  const fitted = Math.min(
    1,
    viewportWidth / (bracketWidth + BRACKET_STAGE_PADDING * 2),
    viewportHeight / (bracketHeight + BRACKET_STAGE_PADDING * 2),
  )
  // 向下取整，避免四舍五入后边缘刚好超出视口。
  return Math.max(0.5, Math.floor(fitted * 100) / 100)
}

export function getBracketStageLayout({
  bracketHeight,
  bracketWidth,
  viewportHeight,
  viewportWidth,
  zoom,
}: BracketStageInput): BracketStageLayout {
  const safeBracketHeight = Math.max(0, finiteOrZero(bracketHeight))
  const safeBracketWidth = Math.max(0, finiteOrZero(bracketWidth))
  const safeViewportHeight = Math.max(0, finiteOrZero(viewportHeight))
  const safeViewportWidth = Math.max(0, finiteOrZero(viewportWidth))
  const safeZoom = zoom > 0 && Number.isFinite(zoom) ? zoom : 1
  const stageWidth = Math.max(
    safeBracketWidth + BRACKET_STAGE_PADDING * 2,
    safeViewportWidth,
  )
  const stageHeight = Math.max(
    safeBracketHeight + BRACKET_STAGE_PADDING * 2,
    safeViewportHeight,
  )

  return {
    stageWidth,
    stageHeight,
    pan: {
      x: (safeViewportWidth - stageWidth * safeZoom) / 2,
      y: (safeViewportHeight - stageHeight * safeZoom) / 2,
    },
  }
}

export function getBracketContentPlacementStyle({
  bracketHeight,
  bracketWidth,
  position,
}: BracketContentPlacementInput): CSSProperties {
  const top =
    position === 'top'
      ? `${BRACKET_STAGE_PADDING}px`
      : position === 'bottom'
        ? `max(${BRACKET_STAGE_PADDING}px, calc(100% - ${bracketHeight}px - ${BRACKET_STAGE_PADDING}px))`
        : `max(${BRACKET_STAGE_PADDING}px, calc((100% - ${bracketHeight}px) / 2))`

  return {
    position: 'absolute',
    left: `max(${BRACKET_STAGE_PADDING}px, calc((100% - ${bracketWidth}px) / 2))`,
    top,
    width: bracketWidth,
    minHeight: bracketHeight,
  }
}
